export const widgetLanguages = ['en', 'cs', 'lv', 'ru'] as const;

export type WidgetLanguage = (typeof widgetLanguages)[number];

export interface WidgetCopy {
  assistantUnavailable: string;
  messageCouldNotBeSent: string;
  assistantStillLoading: string;
  defaultSalonName: string;
  defaultAssistantName: string;
  panelLabel: (salonName: string) => string;
  salonTeamJoined: string;
  onlineStatus: string;
  close: string;
  today: string;
  notSent: string;
  justNow: string;
  servicesAndPrices: string;
  servicesAndPricesPrompt: string;
  findTime: string;
  findTimePrompt: string;
  handoffStatus: string;
  assistantReplying: string;
  restartChat: string;
  retry: string;
  composerPlaceholder: string;
  message: string;
  send: string;
  madeBy: string;
  closeAssistant: string;
  openAssistant: string;
  askAndBook: string;
  bookServiceOn: (
    serviceName: string,
    dateLabel: string,
  ) => string;
  bookServiceEarliest: (serviceName: string) => string;
  from: string;
  relatedServices: string;
  availableOn: string;
  noTimesAvailable: string;
  nextAvailablePrompt: (serviceName: string) => string;
  findNextAvailable: string;
  professional: string;
  chooseSlotPrompt: (time: string, professionalName: string) => string;
  localTestBooking: string;
  bookingConfirmed: string;
  at: string;
  requiredFields: string;
  bookingFailed: string;
  yourAppointment: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  salonNote: string;
  optional: string;
  privacyConsent: string;
  bookingSubmitted: string;
  confirming: string;
  confirmBooking: string;
  paymentAtSalon: string;
  requestTimeout: string;
  networkError: string;
  invalidResponse: string;
  genericRequestError: string;
  chatExpired: string;
  messageInProgress: string;
  chatPaused: string;
  bookingInProgress: string;
  slotUnavailable: string;
  salonNotFound: string;
  invalidRequest: string;
  selectionExpired: string;
}

const copies: Record<WidgetLanguage, WidgetCopy> = {
  en: {
    assistantUnavailable: 'Assistant unavailable.',
    messageCouldNotBeSent: 'Message could not be sent.',
    assistantStillLoading: 'The assistant is still loading.',
    defaultSalonName: 'Salon',
    defaultAssistantName: 'Blinkit assistant',
    panelLabel: (salonName) => `${salonName} assistant`,
    salonTeamJoined: 'Salon team joined',
    onlineStatus: 'Online · replies instantly',
    close: 'Close',
    today: 'Today',
    notSent: 'Not sent',
    justNow: 'Just now',
    servicesAndPrices: 'Services & prices',
    servicesAndPricesPrompt: 'What services and prices do you offer?',
    findTime: 'Find a time',
    findTimePrompt: 'Help me find an available appointment',
    handoffStatus: 'A salon team member is handling this chat.',
    assistantReplying: 'Assistant is replying',
    restartChat: 'Restart chat',
    retry: 'Retry',
    composerPlaceholder: 'Ask about services or book a time…',
    message: 'Message',
    send: 'Send',
    madeBy: 'Made by',
    closeAssistant: 'Close salon assistant',
    openAssistant: 'Open salon assistant',
    askAndBook: 'Ask & book',
    bookServiceOn: (serviceName, dateLabel) =>
      `I’d like to book ${serviceName} for ${dateLabel}.`,
    bookServiceEarliest: (serviceName) =>
      `I’d like to book ${serviceName}. Show me the earliest available time.`,
    from: 'from',
    relatedServices:
      'Here are more services you may like in the same category',
    availableOn: 'Available on',
    noTimesAvailable: 'No times are available on this date.',
    nextAvailablePrompt: (serviceName) =>
      `Please show me the next available time for ${serviceName}.`,
    findNextAvailable: 'Find next available time',
    professional: 'Professional',
    chooseSlotPrompt: (time, professionalName) =>
      `I want ${time} with ${professionalName}`,
    localTestBooking: 'Local test booking',
    bookingConfirmed: 'Booking confirmed',
    at: 'at',
    requiredFields:
      'Please complete all required fields and accept the privacy notice.',
    bookingFailed: 'Booking could not be completed.',
    yourAppointment: 'Your appointment',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    phone: 'Phone',
    salonNote: 'Note for the salon',
    optional: 'Optional',
    privacyConsent:
      'I agree that my details may be used to create this booking.',
    bookingSubmitted: 'Booking submitted',
    confirming: 'Confirming…',
    confirmBooking: 'Confirm booking',
    paymentAtSalon: 'Payment is made at the salon',
    requestTimeout: 'The salon assistant took too long to reply.',
    networkError: 'The salon assistant could not be reached.',
    invalidResponse: 'The salon assistant returned an invalid response.',
    genericRequestError: 'The salon assistant is temporarily unavailable.',
    chatExpired: 'This chat session has expired. Please start a new chat.',
    messageInProgress:
      'Please wait for the current reply before sending another message.',
    chatPaused:
      'A salon team member is handling this chat. Please ask them to complete the booking.',
    bookingInProgress: 'This booking is already being confirmed.',
    slotUnavailable:
      'That appointment was just taken. Please choose another available time.',
    salonNotFound: 'The salon could not be found.',
    invalidRequest: 'Please check the entered information and try again.',
    selectionExpired:
      'This selection has expired. Please choose an available option again.',
  },
  cs: {
    assistantUnavailable: 'Asistent není dostupný.',
    messageCouldNotBeSent: 'Zprávu se nepodařilo odeslat.',
    assistantStillLoading: 'Asistent se stále načítá.',
    defaultSalonName: 'Salon',
    defaultAssistantName: 'Asistent Blinkit',
    panelLabel: (salonName) => `Asistent salonu ${salonName}`,
    salonTeamJoined: 'Připojil se tým salonu',
    onlineStatus: 'Online · odpovídá okamžitě',
    close: 'Zavřít',
    today: 'Dnes',
    notSent: 'Neodesláno',
    justNow: 'Právě teď',
    servicesAndPrices: 'Služby a ceny',
    servicesAndPricesPrompt: 'Jaké služby nabízíte a jaké jsou ceny?',
    findTime: 'Najít termín',
    findTimePrompt: 'Pomozte mi najít volný termín',
    handoffStatus: 'Tento chat nyní vyřizuje pracovník salonu.',
    assistantReplying: 'Asistent odpovídá',
    restartChat: 'Začít nový chat',
    retry: 'Zkusit znovu',
    composerPlaceholder: 'Zeptejte se na služby nebo si rezervujte termín…',
    message: 'Zpráva',
    send: 'Odeslat',
    madeBy: 'Vytvořil',
    closeAssistant: 'Zavřít asistenta salonu',
    openAssistant: 'Otevřít asistenta salonu',
    askAndBook: 'Dotaz a rezervace',
    bookServiceOn: (serviceName, dateLabel) =>
      `Chci rezervovat službu ${serviceName} na ${dateLabel}.`,
    bookServiceEarliest: (serviceName) =>
      `Chci rezervovat službu ${serviceName}. Ukažte mi nejbližší volný termín.`,
    from: 'od',
    relatedServices:
      'Další služby ze stejné kategorie, které by se vám mohly líbit',
    availableOn: 'Volné termíny',
    noTimesAvailable: 'V tento den nejsou volné žádné termíny.',
    nextAvailablePrompt: (serviceName) =>
      `Ukažte mi prosím nejbližší volný termín pro službu ${serviceName}.`,
    findNextAvailable: 'Najít nejbližší volný termín',
    professional: 'Specialista',
    chooseSlotPrompt: (time, professionalName) =>
      `Chci termín v ${time} u ${professionalName}`,
    localTestBooking: 'Místní testovací rezervace',
    bookingConfirmed: 'Rezervace potvrzena',
    at: 'v',
    requiredFields:
      'Vyplňte všechna povinná pole a přijměte upozornění o ochraně soukromí.',
    bookingFailed: 'Rezervaci se nepodařilo dokončit.',
    yourAppointment: 'Váš termín',
    firstName: 'Jméno',
    lastName: 'Příjmení',
    email: 'E-mail',
    phone: 'Telefon',
    salonNote: 'Poznámka pro salon',
    optional: 'Volitelné',
    privacyConsent:
      'Souhlasím s použitím svých údajů pro vytvoření této rezervace.',
    bookingSubmitted: 'Rezervace odeslána',
    confirming: 'Potvrzování…',
    confirmBooking: 'Potvrdit rezervaci',
    paymentAtSalon: 'Platba proběhne v salonu',
    requestTimeout: 'Odpověď asistenta salonu trvala příliš dlouho.',
    networkError: 'K asistentovi salonu se nepodařilo připojit.',
    invalidResponse: 'Asistent salonu vrátil neplatnou odpověď.',
    genericRequestError: 'Asistent salonu je dočasně nedostupný.',
    chatExpired: 'Platnost tohoto chatu vypršela. Začněte nový chat.',
    messageInProgress:
      'Před odesláním další zprávy počkejte na aktuální odpověď.',
    chatPaused:
      'Tento chat vyřizuje pracovník salonu. Požádejte ho o dokončení rezervace.',
    bookingInProgress: 'Tato rezervace se již potvrzuje.',
    slotUnavailable:
      'Tento termín byl právě obsazen. Vyberte si jiný volný termín.',
    salonNotFound: 'Salon se nepodařilo najít.',
    invalidRequest: 'Zkontrolujte zadané údaje a zkuste to znovu.',
    selectionExpired:
      'Platnost tohoto výběru vypršela. Vyberte znovu některou z dostupných možností.',
  },
  lv: {
    assistantUnavailable: 'Asistents nav pieejams.',
    messageCouldNotBeSent: 'Ziņojumu neizdevās nosūtīt.',
    assistantStillLoading: 'Asistents joprojām tiek ielādēts.',
    defaultSalonName: 'Salons',
    defaultAssistantName: 'Blinkit asistents',
    panelLabel: (salonName) => `${salonName} asistents`,
    salonTeamJoined: 'Pievienojās salona komanda',
    onlineStatus: 'Tiešsaistē · atbild uzreiz',
    close: 'Aizvērt',
    today: 'Šodien',
    notSent: 'Nav nosūtīts',
    justNow: 'Tikko',
    servicesAndPrices: 'Pakalpojumi un cenas',
    servicesAndPricesPrompt:
      'Kādus pakalpojumus jūs piedāvājat un kādas ir cenas?',
    findTime: 'Atrast laiku',
    findTimePrompt: 'Palīdziet man atrast brīvu pieraksta laiku',
    handoffStatus: 'Šo sarunu apkalpo salona darbinieks.',
    assistantReplying: 'Asistents atbild',
    restartChat: 'Sākt jaunu sarunu',
    retry: 'Mēģināt vēlreiz',
    composerPlaceholder: 'Jautājiet vai rezervējiet laiku…',
    message: 'Ziņojums',
    send: 'Nosūtīt',
    madeBy: 'Izveidoja',
    closeAssistant: 'Aizvērt salona asistentu',
    openAssistant: 'Atvērt salona asistentu',
    askAndBook: 'Jautāt un rezervēt',
    bookServiceOn: (serviceName, dateLabel) =>
      `Vēlos rezervēt pakalpojumu “${serviceName}” datumā ${dateLabel}.`,
    bookServiceEarliest: (serviceName) =>
      `Vēlos rezervēt pakalpojumu “${serviceName}”. Parādiet tuvāko pieejamo laiku.`,
    from: 'no',
    relatedServices:
      'Citi tās pašas kategorijas pakalpojumi, kas jums varētu patikt',
    availableOn: 'Pieejams',
    noTimesAvailable: 'Šajā datumā nav pieejamu laiku.',
    nextAvailablePrompt: (serviceName) =>
      `Lūdzu, parādiet nākamo pieejamo laiku pakalpojumam “${serviceName}”.`,
    findNextAvailable: 'Atrast nākamo pieejamo laiku',
    professional: 'Speciālists',
    chooseSlotPrompt: (time, professionalName) =>
      `Vēlos laiku plkst. ${time} pie ${professionalName}`,
    localTestBooking: 'Lokāla testa rezervācija',
    bookingConfirmed: 'Rezervācija apstiprināta',
    at: 'plkst.',
    requiredFields:
      'Aizpildiet visus obligātos laukus un piekrītiet privātuma paziņojumam.',
    bookingFailed: 'Rezervāciju neizdevās pabeigt.',
    yourAppointment: 'Jūsu pieraksts',
    firstName: 'Vārds',
    lastName: 'Uzvārds',
    email: 'E-pasts',
    phone: 'Tālrunis',
    salonNote: 'Piezīme salonam',
    optional: 'Neobligāti',
    privacyConsent:
      'Piekrītu, ka mani dati var tikt izmantoti šīs rezervācijas izveidei.',
    bookingSubmitted: 'Rezervācija nosūtīta',
    confirming: 'Apstiprina…',
    confirmBooking: 'Apstiprināt rezervāciju',
    paymentAtSalon: 'Maksājums tiek veikts salonā',
    requestTimeout: 'Salona asistenta atbilde aizņēma pārāk ilgu laiku.',
    networkError: 'Neizdevās izveidot savienojumu ar salona asistentu.',
    invalidResponse: 'Salona asistents atgrieza nederīgu atbildi.',
    genericRequestError: 'Salona asistents īslaicīgi nav pieejams.',
    chatExpired: 'Šīs sarunas sesija ir beigusies. Sāciet jaunu sarunu.',
    messageInProgress:
      'Pirms nākamā ziņojuma nosūtīšanas sagaidiet pašreizējo atbildi.',
    chatPaused:
      'Šo sarunu apkalpo salona darbinieks. Lūdziet viņam pabeigt rezervāciju.',
    bookingInProgress: 'Šī rezervācija jau tiek apstiprināta.',
    slotUnavailable:
      'Šis pieraksta laiks tikko tika aizņemts. Izvēlieties citu pieejamu laiku.',
    salonNotFound: 'Salonu neizdevās atrast.',
    invalidRequest: 'Pārbaudiet ievadīto informāciju un mēģiniet vēlreiz.',
    selectionExpired:
      'Šīs izvēles derīguma termiņš ir beidzies. Izvēlieties vēlreiz kādu no pieejamajām iespējām.',
  },
  ru: {
    assistantUnavailable: 'Ассистент недоступен.',
    messageCouldNotBeSent: 'Не удалось отправить сообщение.',
    assistantStillLoading: 'Ассистент ещё загружается.',
    defaultSalonName: 'Салон',
    defaultAssistantName: 'Ассистент Blinkit',
    panelLabel: (salonName) => `Ассистент салона ${salonName}`,
    salonTeamJoined: 'Команда салона подключилась',
    onlineStatus: 'Онлайн · отвечает мгновенно',
    close: 'Закрыть',
    today: 'Сегодня',
    notSent: 'Не отправлено',
    justNow: 'Только что',
    servicesAndPrices: 'Услуги и цены',
    servicesAndPricesPrompt:
      'Какие услуги вы предлагаете и сколько они стоят?',
    findTime: 'Найти время',
    findTimePrompt: 'Помогите найти свободное время для записи',
    handoffStatus: 'Эту переписку ведёт сотрудник салона.',
    assistantReplying: 'Ассистент отвечает',
    restartChat: 'Начать новый чат',
    retry: 'Повторить',
    composerPlaceholder: 'Спросите об услугах или запишитесь на приём…',
    message: 'Сообщение',
    send: 'Отправить',
    madeBy: 'Создано',
    closeAssistant: 'Закрыть ассистента салона',
    openAssistant: 'Открыть ассистента салона',
    askAndBook: 'Спросить и записаться',
    bookServiceOn: (serviceName, dateLabel) =>
      `Я хочу записаться на «${serviceName}» на ${dateLabel}.`,
    bookServiceEarliest: (serviceName) =>
      `Я хочу записаться на «${serviceName}». Покажите ближайшее свободное время.`,
    from: 'от',
    relatedServices:
      'Другие услуги из этой категории, которые могут вам понравиться',
    availableOn: 'Доступно',
    noTimesAvailable: 'На эту дату нет свободного времени.',
    nextAvailablePrompt: (serviceName) =>
      `Покажите, пожалуйста, ближайшее свободное время для услуги «${serviceName}».`,
    findNextAvailable: 'Найти ближайшее свободное время',
    professional: 'Специалист',
    chooseSlotPrompt: (time, professionalName) =>
      `Хочу на ${time} к ${professionalName}`,
    localTestBooking: 'Локальная тестовая запись',
    bookingConfirmed: 'Запись подтверждена',
    at: 'в',
    requiredFields:
      'Заполните все обязательные поля и примите уведомление о конфиденциальности.',
    bookingFailed: 'Не удалось завершить запись.',
    yourAppointment: 'Ваша запись',
    firstName: 'Имя',
    lastName: 'Фамилия',
    email: 'Эл. почта',
    phone: 'Телефон',
    salonNote: 'Примечание для салона',
    optional: 'Необязательно',
    privacyConsent:
      'Я согласен, что мои данные могут использоваться для создания этой записи.',
    bookingSubmitted: 'Запись отправлена',
    confirming: 'Подтверждение…',
    confirmBooking: 'Подтвердить запись',
    paymentAtSalon: 'Оплата производится в салоне',
    requestTimeout: 'Ассистент салона слишком долго не отвечал.',
    networkError: 'Не удалось связаться с ассистентом салона.',
    invalidResponse: 'Ассистент салона вернул некорректный ответ.',
    genericRequestError: 'Ассистент салона временно недоступен.',
    chatExpired: 'Срок действия этого чата истёк. Начните новый чат.',
    messageInProgress:
      'Дождитесь текущего ответа, прежде чем отправлять следующее сообщение.',
    chatPaused:
      'Эту переписку ведёт сотрудник салона. Попросите его завершить запись.',
    bookingInProgress: 'Эта запись уже подтверждается.',
    slotUnavailable:
      'Это время только что заняли. Выберите другое доступное время.',
    salonNotFound: 'Не удалось найти салон.',
    invalidRequest: 'Проверьте введённые данные и повторите попытку.',
    selectionExpired:
      'Срок действия этого выбора истёк. Выберите доступный вариант ещё раз.',
  },
};

const errorCopyKeys: Record<string, keyof WidgetCopy> = {
  SESSION_EXPIRED: 'chatExpired',
  VISITOR_TOKEN_EXPIRED: 'chatExpired',
  VISITOR_TOKEN_INVALID: 'chatExpired',
  VISITOR_TOKEN_REQUIRED: 'chatExpired',
  MESSAGE_IN_PROGRESS: 'messageInProgress',
  CHAT_PAUSED: 'chatPaused',
  BOOKING_IN_PROGRESS: 'bookingInProgress',
  SLOT_UNAVAILABLE: 'slotUnavailable',
  SALON_NOT_FOUND: 'salonNotFound',
  BAD_REQUEST: 'invalidRequest',
  INVALID_REQUEST: 'invalidRequest',
  SLOT_NOT_FOUND: 'selectionExpired',
  SERVICE_NOT_FOUND: 'selectionExpired',
};

export function resolveWidgetLanguage(locale: string): WidgetLanguage {
  const language = locale.trim().replace(/_/g, '-').split('-')[0]
    .toLowerCase();
  return widgetLanguages.includes(language as WidgetLanguage)
    ? language as WidgetLanguage
    : 'en';
}

export function getWidgetCopy(locale: string): WidgetCopy {
  return copies[resolveWidgetLanguage(locale)];
}

export function getWidgetErrorMessage(
  locale: string,
  code: string,
  fallback?: string,
): string {
  const language = resolveWidgetLanguage(locale);
  const copy = copies[language];
  if (language === 'en' && fallback?.trim()) return fallback.trim();
  const key = errorCopyKeys[code];
  if (key) {
    const value = copy[key];
    if (typeof value === 'string') return value;
  }
  return copy.genericRequestError;
}
